import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {PopoverControllerService} from '../../../../shared/shared-services/overlay/popover-controller.service';
import {
  SelectPopoverComponent,
} from '../../../../shared-modules/shared-module/components/select-popover/select-popover.component';
import {
  CollectionStoresSettingsService,
} from '../../../../shared/shared-services/firebase/collection-stores-settings.service';
import {path_stores_storeId_settings_disable_stock_item_rules} from '../../../../shared/shared-services/database-paths';
import {IModalDismissResult, ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IDisableStockItemRule, IOnHandDisableRule, TLineColour} from '../../../settings-new/models';
import {decodeOnHoldCode} from '../../../../shared/shared-module-ui/utils/on-hold-code/on-hold-code.utils';
import {_ON_HOLD_TYPES} from '../../../settings-new/models/on-hold-code/on-hold-code.interface';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';

@Component({
  selector: 'app-del-item-settings',
  templateUrl: './del-item-settings.component.html',
  styleUrls: ['./del-item-settings.component.scss'],
  standalone: false,
})
export class DelItemSettingsComponent implements OnInit, OnChanges {

  @Input() storeId: string;
  @Input() settings: IDisableStockItemRule;
  @Input() lineColours: TLineColour;
  @Input() isModal?: boolean = true;

  hide: Record<string, boolean> = {};
  saving = false;

  newSettings: IDisableStockItemRule = {onHandZero: {}, onHandNotZero: {}};

  readonly onHoldKeys: string[];
  readonly icons = IconsEnum;
  readonly iconColourEnum = IconColourEnum;

  constructor(
    private modalController: ModalControllerService,
    private popoverControl: PopoverControllerService,
    private collectionStoresSettingsService: CollectionStoresSettingsService,
  ) {
    this.onHoldKeys = Object.keys({..._ON_HOLD_TYPES}).sort((a: string, b: string) => +a < +b ? -1 : 1);
  }

  ngOnInit(): void {

    if (this.settings) {
      Object.keys(this.settings).forEach(i => {
        this.newSettings[i] = {};
        Object.keys(this.settings[i] as IOnHandDisableRule).forEach(j => {
          this.newSettings[i][j] = this.settings[i][j];
        });
      });
    }

    if (!this.newSettings) {
      this.newSettings = {onHandZero: {}, onHandNotZero: {}};
    } else {
      if (!this.newSettings.onHandZero) {
        this.newSettings.onHandZero = {};
      }
      if (!this.newSettings.onHandNotZero) {
        this.newSettings.onHandNotZero = {};
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings']?.currentValue) {
      const currentValue = changes['settings'].currentValue;
      this.updateNavData(currentValue as IDisableStockItemRule);
    }
  }

  updateNavData(currentValue: IDisableStockItemRule): void {
    this.settings = currentValue;
    for (const i of Object.keys(this.settings)) {
      this.newSettings[i] = {};
      for (const j of Object.keys(this.settings[i] as IOnHandDisableRule)) {
        this.newSettings[i][j] = this.settings[i][j];
      }
    }
  }

  close(): void {
    void this.modalController.dismiss().then();
  }

  async select(event: Event, part: string): Promise<void> {
    const value: string[] = this.newSettings[part].onHoldCode ?
      decodeOnHoldCode(+this.newSettings[part].onHoldCode, {returnCodes: true}) : null;
    const pop = await this.popoverControl.create({
      component: SelectPopoverComponent,
      componentProps: {
        title: 'On Hold Types',
        selection: {..._ON_HOLD_TYPES},
        order: this.onHoldKeys,
        value,
        multiple: true,
        selectAll: true,
        selectModButtons: [{
          text: `All but 'Invoices & CN'`,
          handler: (checks: Record<string, boolean>): Record<string, boolean> => {
            for (const sKey of this.onHoldKeys) {
              checks[sKey] = true;
            }
            checks['1'] = false;
            checks['2'] = false;
            return checks;
          },
        }],
      }, event,
    });
    void pop.onDidDismiss().then((response: IModalDismissResult) => {
      const selection: string[] = response.data;

      if (selection) {
        if (selection.length) {
          this.newSettings[part].onHoldCode = selection.reduce((acc, v) => '' + (+acc + +v));
        } else {
          this.newSettings[part].onHoldCode = null;
        }
      }
    });
    await pop.present();
  }

  async selectColour(event: Event, part: string): Promise<void> {
    const selection = {};
    const selectionStyles = {};
    const value = this.newSettings[part].lineColour ? [this.newSettings[part].lineColour] : null;
    const lineColoursOrder = Object.keys(this.lineColours).sort();

    for (const n of lineColoursOrder) {
      selection[n] = `(${n}) ${this.lineColours[n].description}`;
      selectionStyles[n] = `background: ${this.lineColours[n].value}`;
    }

    const pc = await this.popoverControl.create({
      component: SelectPopoverComponent,
      componentProps: {
        title: 'Line Colour', selection, selectionStyles, order: lineColoursOrder, value,
      },
      event,
    });
    await pc.present();
    const response = await pc.onDidDismiss<string[]>();

    if (response.data) {
      this.newSettings[part].lineColour = response.data;
    }
  }

  valid(): boolean {
    const s = this.newSettings;

    if (s.onHandZero) {
      if (s.onHandZero.descPrefix || s.onHandZero.descSuffix || s.onHandZero.onHoldCode) {
        return true;
      }
    }
    if (s.onHandNotZero) {
      if (s.onHandNotZero.descPrefix || s.onHandNotZero.descSuffix || s.onHandNotZero.onHoldCode) {
        return true;
      }
    }
    return false;
  }

  save(): void {
    if (this.valid()) {
      this.saving = true;
      for (const rule of Object.keys(this.newSettings)) {
        for (const vKey of Object.keys(this.newSettings[rule] as IOnHandDisableRule)) {
          if (!this.newSettings[rule][vKey]) {
            delete this.newSettings[rule][vKey];
          } else if (['onHoldCode', 'lineColour'].includes(vKey)) {
            this.newSettings[rule][vKey] = +this.newSettings[rule][vKey];
          }
        }
        if (Object.keys(this.newSettings[rule] as IOnHandDisableRule).length === 0) {
          delete this.newSettings[rule];
        }
      }
      const update = this.newSettings;
      this.collectionStoresSettingsService.setDocument<IDisableStockItemRule>(path_stores_storeId_settings_disable_stock_item_rules(this.storeId), update).then((r) => {
        this.saving = false;
      }).catch((err) => {
        window.alert('ERROR:\n' + err.toString());
        this.saving = false;
      });
    }
  }


}
